// PhotoUpload.tsx
import React, {useState, useRef, memo} from 'react';
import LazyAssets from '@/app/utils/assets_widget';
import styles from './styles.module.scss';
import classNames from "classnames";

interface WidgetProps {
  onUpload: (data: string) => void;
  link?: string;
  name?: string;
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic'];
const MAX_SIZE = 1024 * 1024;

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result as string);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

export const compressFile = (
  file: File,
  {
    quality = 0.7,
    maxWidth = 1024,
    maxHeight = 1024,
    outputType = 'image/webp'
  }: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    outputType?: string;
  } = {}
): Promise<string> =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onerror = rej;
    reader.onload = () => {
      const img = new Image();
      img.onerror = rej;
      img.onload = () => {
        let {width, height} = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob => {
            if (!blob) return rej(new Error('Compression failed'));
            const fr = new FileReader();
            fr.onerror = rej;
            fr.onload = () => res(fr.result as string);
            fr.readAsDataURL(blob);
          },
          outputType,
          quality
        );
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

const PhotoUpload: React.FC<WidgetProps> = ({onUpload, link, name}) => {
  const [data, setData] = useState<string>();
  const [cleared, setCleared] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pickFile = () => inputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // if (!ALLOWED_TYPES.includes(file.type)) {
    //   alert('Недопустимый формат файла');
    //   return;
    // }
    let b64: string;
    if (file.size > MAX_SIZE) b64 = await compressFile(file);
    else b64 = await fileToBase64(file);

    setData(b64);
    setCleared(false);
    onUpload(b64);
    e.target.value = '';
  };

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setData(undefined);
    setCleared(true);
  };

  let content: React.ReactNode;

  if (data) {
    content = (
      <>
        <img src={data} className={styles.img}/>
        <div className={styles.roundButton} onClick={onClear}>
          <LazyAssets.trash/>
        </div>
      </>
    );
  } else if (link && !cleared) {
    content = (
      <>
        <img src={link} className={styles.img}/>
        <div className={styles.roundButton} onClick={onClear}>
          <LazyAssets.trash/>
        </div>
      </>
    );
  } else if (name) {
    content = <>
      <span className={styles.initial}>{name.charAt(0).toUpperCase()}</span>
      <div className={styles.roundButton}>
        <LazyAssets.AddAPhoto/>
      </div>
    </>

  } else {
    content = <LazyAssets.AddAPhoto className={styles.icon}/>;
  }

  return (
    <div className={classNames(styles.container, {[styles.empty]: !(data && link && !cleared) && !name})}
         onClick={pickFile}>
      {content}
      <input
        type="file"
        ref={inputRef}
        accept=".png,.jpg,.jpeg,.webp,.heic"
        className={styles.input}
        onChange={onFileChange}
      />
    </div>
  );
};

export default memo(PhotoUpload);
